import {
  getPortfolioAggregate,
  getPortfolioProjects,
} from "../portfolio-dashboard/api/portfolio";
import {
  getPortfolioAggregateByYear,
  getPortfolioAirshedAggregate,
  getPortfolioLifetime,
} from "./api";
import type { PortfolioReportData, PortfolioReportOptions } from "./types";

/**
 * Resolve everything a portfolio report needs from the existing portfolio
 * endpoints, given the builder options. On-demand (mirrors the project
 * report's data extractor) — called by the generator, not a React hook.
 */
export async function loadPortfolioReportData(
  token: string,
  options: PortfolioReportOptions,
): Promise<PortfolioReportData> {
  const { filters, analysis, timeBasis } = options;

  // Detail pages (emissions, sub-categories, fuel) always come from the
  // full-period summary_json aggregate — these are not available per-year.
  const [aggregate, projects] = await Promise.all([
    getPortfolioAggregate(token, filters, analysis),
    getPortfolioProjects(token, {
      filters,
      sort: options.rankingMetric === "per_mw" ? "dalys" : options.rankingMetric,
      limit: 500, // API hard cap; current portfolios are well under this
    }),
  ]);

  let headline: PortfolioReportData["headline"] = {
    basisLabel: "Lifetime (realized)",
    dalys: aggregate.impacts.Total.DALYs,
    pdfs: aggregate.impacts.Total.PDFs,
    usd: aggregate.impacts.Total.USD,
    generationMwh: Object.values(aggregate.generation).reduce(
      (s, g) => s + (g?.Difference ?? 0),
      0,
    ),
    isProjection: false,
  };

  let year;
  let lifetime;

  // The year / projection aggregations are optional refinements. If one
  // fails or times out, degrade to the always-available full-period
  // headline rather than failing the whole report.
  if (timeBasis === "single_year" && options.year != null) {
    try {
      year = await getPortfolioAggregateByYear(token, options.year, filters);
      if (year.tsdb_available) {
        headline = {
          basisLabel: String(options.year),
          dalys: year.dalys,
          pdfs: year.pdfs,
          usd: year.usd,
          generationMwh: year.total_generation_mwh,
          isProjection: false,
        };
      } else {
        headline.basisLabel = `Lifetime (per-year ${options.year} unavailable)`;
      }
    } catch (e) {
      console.warn("aggregate-by-year failed, using full-period:", e);
      headline.basisLabel = `Lifetime (per-year ${options.year} unavailable)`;
    }
  } else if (timeBasis === "projection") {
    try {
      lifetime = await getPortfolioLifetime(token, filters, {
        lifetimeYears: options.lifetimeYears,
        discountRate: options.discountRate,
      });
      headline = {
        basisLabel: `${lifetime.lifetime_years}-yr projection`,
        dalys: lifetime.dalys_nominal,
        pdfs: lifetime.pdfs_nominal,
        usd: lifetime.usd_nominal,
        generationMwh: lifetime.total_generation_mwh,
        isProjection: true,
        npv: {
          dalys: lifetime.dalys_npv,
          pdfs: lifetime.pdfs_npv,
          usd: lifetime.usd_npv,
        },
      };
    } catch (e) {
      console.warn("portfolio lifetime failed, using full-period:", e);
      headline.basisLabel = "Lifetime (projection unavailable)";
    }
  }

  const netBurdenCount = projects.filter(
    (p) => (p.dalys ?? 0) < 0,
  ).length;

  let spatial: PortfolioReportData["spatial"];
  let usStatesFC: PortfolioReportData["usStatesFC"];
  if (options.sections.spatial_map) {
    const r = await getPortfolioAirshedAggregate(token, "state", filters);
    spatial = r.regions;
    usStatesFC = r.featureCollection;
  }

  return {
    options,
    generatedAt: new Date().toISOString(),
    aggregate,
    headline,
    year,
    lifetime,
    projects,
    netBurdenCount,
    spatial,
    usStatesFC,
  };
}
